import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Save, Eye, RotateCcw, Loader2, Code2, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKENS = [
  "{{logo}}", "{{empresa.nome}}", "{{empresa.cnpj}}",
  "{{empresa.endereco}}", "{{empresa.site}}", "{{pagina}}",
];

const HeaderFooterEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [header, setHeader] = useState("");
  const [footer, setFooter] = useState("");
  const [bgColor, setBgColor] = useState("");
  const [logoSize, setLogoSize] = useState(40);
  const [padrao, setPadrao] = useState({ header: "", footer: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documentos/header-footer`);
      setHeader(data.header || data.headerPadrao || "");
      setFooter(data.footer || data.footerPadrao || "");
      setBgColor(data.bgColor || "");
      setLogoSize(data.logoSize || 40);
      setPadrao({ header: data.headerPadrao || "", footer: data.footerPadrao || "" });
    } catch (e) {
      toast.error("Falha ao carregar cabeçalho/rodapé.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/documentos/header-footer`, { header, footer, bgColor, logoSize: Number(logoSize) || 40 });
      toast.success("Cabeçalho e rodapé salvos. Valem para todos os PDFs (contrato e documentos).");
    } catch (e) {
      toast.error("Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const preVisualizar = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API}/documentos/header-footer/preview`,
        { header, footer, bgColor, logoSize: Number(logoSize) || 40 },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Erro na pré-visualização.");
    } finally { setPreviewing(false); }
  };

  const restaurar = () => {
    setHeader(padrao.header);
    setFooter(padrao.footer);
    setBgColor("");
    setLogoSize(40);
    toast.info("Padrão restaurado. Clique em Salvar para confirmar.");
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>;
  }

  return (
    <div className="space-y-4" data-testid="header-footer-editor">
      <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-xs text-slate-600 leading-relaxed">
          Edite o HTML do cabeçalho e do rodapé de <b>todos os PDFs</b> (contrato e documentos). Use estilos inline
          (ex.: <code>style="background-color:#1e5aa8;color:#fff;text-align:center"</code>) para cor de fundo, posicionamento e tamanho.
          Tokens disponíveis (clique para copiar):
          <div className="flex flex-wrap gap-1.5 mt-2">
            {TOKENS.map((t) => (
              <button key={t} type="button" onClick={() => { navigator.clipboard?.writeText(t); toast.success(`Copiado: ${t}`); }}
                className="px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-700 font-mono text-[11px] hover:bg-blue-100" data-testid={`token-${t}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><Code2 className="w-4 h-4" /> Cabeçalho (HTML)</Label>
          <textarea value={header} onChange={(e) => setHeader(e.target.value)} rows={8}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            data-testid="header-html-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><Code2 className="w-4 h-4" /> Rodapé (HTML)</Label>
          <textarea value={footer} onChange={(e) => setFooter(e.target.value)} rows={8}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            data-testid="footer-html-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-5">
        <div className="space-y-1">
          <Label>Tamanho do logo (px)</Label>
          <Input type="number" min="10" max="120" value={logoSize} onChange={(e) => setLogoSize(e.target.value)} className="w-28" data-testid="logo-size-input" />
        </div>
        <div className="space-y-1">
          <Label>Cor de fundo do documento</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={bgColor || "#ffffff"} onChange={(e) => setBgColor(e.target.value)} className="w-10 h-9 rounded border border-slate-200 cursor-pointer" data-testid="bg-color-input" />
            <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => setBgColor("")} data-testid="bg-color-clear">Sem cor</Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <Button variant="outline" className="gap-2 text-slate-600" onClick={restaurar} data-testid="hf-restaurar-btn">
          <RotateCcw className="w-4 h-4" /> Restaurar padrão
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={preVisualizar} disabled={previewing} data-testid="hf-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Pré-visualizar
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} disabled={saving} data-testid="hf-salvar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HeaderFooterEditor;
